import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Code2, KeyRound, Terminal } from 'lucide-react'
import { useSeoHead } from '../lib/seo'

const BASE = 'https://apitools.seo-solution.cz/api/v1/ext'

const ENDPOINTS = [
  { m: 'GET', p: '/me', d: 'Informace o účtu (ověření tokenu).' },
  { m: 'POST', p: '/analyze', d: 'Kompletní SEO analýza webu.', body: '{ "url": "https://example.com" }' },
  { m: 'GET', p: '/rank/projects', d: 'Seznam projektů sledování pozic.' },
  { m: 'GET', p: '/rank/projects/{id}', d: 'Detail projektu — klíčová slova a pozice (per vyhledávač i lokace).' },
  { m: 'GET', p: '/rank/projects/{id}/dashboard', d: 'Dashboard pozic projektu.' },
  { m: 'POST', p: '/tools/{tool}', d: 'Spustí SEO nástroj. {tool} = robots, sitemap, schema, serp, hreflang, mobile, security, pagespeed, redirects, content, technology, accessibility, ux, cookies, dns, broken-links, geo.', body: '{ "url": "https://example.com" }' },
]

function Code({ children }: { children: string }) {
  return (
    <pre className="bg-gray-950 text-gray-200 rounded-xl p-4 text-xs overflow-x-auto font-mono leading-relaxed">{children}</pre>
  )
}

export default function ApiDocsPage() {
  const { t } = useTranslation()
  useSeoHead({
    title: t('apidocs.meta_title', { defaultValue: 'API dokumentace | SEO Tools' }),
    description: t('apidocs.meta_desc', { defaultValue: 'Programový přístup k SEO Tools API, analýza webu, sledování pozic a SEO nástroje přes REST API s tokenem.' }),
    canonicalPath: '/api-docs',
  })

  const mColor = (m: string) => m === 'GET' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-600 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/25">
          <Code2 className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">{t('apidocs.title', { defaultValue: 'API dokumentace' })}</h1>
      </div>
      <p className="text-gray-500 dark:text-gray-400 mb-8">{t('apidocs.subtitle', { defaultValue: 'REST API pro analýzu webu, sledování pozic a SEO nástroje.' })}</p>

      {/* Auth */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary-500" />{t('apidocs.auth', { defaultValue: 'Autentizace' })}</h2>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
          {t('apidocs.auth_desc', { defaultValue: 'Vytvořte si token v Profilu → API tokeny a posílejte ho v hlavičce. Váš plán musí mít povolený API přístup.' })}{' '}
          <Link to="/profile" className="text-primary-600 hover:underline">{t('apidocs.create_token', { defaultValue: 'Vytvořit token' })}</Link>
        </p>
        <Code>{`Authorization: Bearer seo_xxxxxxxxxxxxxxxxxxxxxxxx
# nebo
X-Api-Key: seo_xxxxxxxxxxxxxxxxxxxxxxxx`}</Code>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Base URL: <code className="text-primary-600">{BASE}</code></p>
      </section>

      {/* Endpoints */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"><Terminal className="w-5 h-5 text-primary-500" />{t('apidocs.endpoints', { defaultValue: 'Endpointy' })}</h2>
        <div className="space-y-3">
          {ENDPOINTS.map((e) => (
            <div key={e.m + e.p} className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${mColor(e.m)}`}>{e.m}</span>
                <code className="text-sm font-mono text-gray-800 dark:text-gray-200">{e.p}</code>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{e.d}</p>
              {e.body && <p className="text-xs text-gray-500 mt-1.5">Body (JSON): <code className="font-mono">{e.body}</code></p>}
            </div>
          ))}
        </div>
      </section>

      {/* Příklad */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{t('apidocs.example', { defaultValue: 'Příklad (curl)' })}</h2>
        <Code>{`# SEO analýza webu
curl -X POST "${BASE}/analyze" \\
  -H "Authorization: Bearer seo_xxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://example.com"}'

# Sledování pozic — seznam projektů
curl "${BASE}/rank/projects" \\
  -H "Authorization: Bearer seo_xxxxxxxxxxxxxxxxxxxxxxxx"

# Spuštění nástroje (robots.txt)
curl -X POST "${BASE}/tools/robots" \\
  -H "Authorization: Bearer seo_xxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://example.com"}'`}</Code>
      </section>
    </div>
  )
}
